import React, { useEffect } from "react";
import { BCMSDate, DisabledPrompt, useBCMSContext, BCMSStateManagerCRUD, useStateManager, BCMSRecord, StateManager, useRoute, useRouteData } from "bcms-react";
import Classes, { Account, CompanyAccount, ACHTemplate, ACHFromAccount, ACHToParticipant } from "model";
import "./styles.css";
import "./mobile.css";
import { SVGAsset } from "res";
import { TableItemTextView, TableItemView, TableItemContainer, TableView, FormContainer, ContentHeaderView, CenterContentContainer, PageContentView, PageContainer, FormRowView, TableColumnContainer, TableColumnView, TableBody } from "components/shared";
import { PrimaryButton, SecondaryButton, UICheckbox, UITextInput } from "components/ui";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import DateSelect from "components/custom-ui/DateSelect";
import MobileTemplateACHTable from "./components/MobileTemplateACHTable";
import ACHTemplateSearch from "./classes/ACHTemplateSearch";
import { ConfirmBannerView } from "components/shared";

/**
 * @function TemplatesACHPage 
 * @returns  {React.Component}
 * @exported true
 * @description The main page component for ACH Templates.
 */
export default function TemplatesACHPage(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      onChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", prop ], value)
        .submit(setState);
      },

      onSearchChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "search", prop ], value)
        .submit(setState);
      }
  });
  
  return(
    <PageContainer className="templates-ach-page" state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = () => ({
  achTemplates: ACHTemplate.initAll(), // achTemplates need to be added to the model.
  companyAccounts: CompanyAccount.initAll(),
  businessAccounts: Account.initAllBusiness(),
  showSearchOptions: false,
  allRowsChecked: false,
  search: ACHTemplateSearch.init(),
  // Example of setting search values.
  /*search: ACHTemplateSearch.init({
    companyID: "0", 
    transactionType: "ccd credit - non-consumer credit",
    participantName: "some participant name", 
    templateName: "some template name", 
    offsetAccountID: "unbalanced"
  })*/
    createdTemplate: undefined
});
export const getDefaultUI = () => ({
  // no ui changes for now
  showCreatedACHTemplateBanner: false,
  searchOptions: { show: true },
  addTemplateButton: { show: true }
});

/**
 * @function PageContent
 * @returns {React.Component}
 * @exported false
 * @description Content for the page. Created in order to use hook "useStateManager".
 */
function PageContent(props) {
  const { data, ui, onChange, onSearchChange } = useStateManager();
  const templates = data.achTemplates;
  const hasACHTemplates = templates.length > 0;
  const navigate = useRoute();
  const search = data.search ?? {};
  const { route } = useRouteData();
  let showACHTemplateCreatedBanner = ui.showCreatedACHTemplateBanner ?? false;
  let createdTemplate = data.createdTemplate??  undefined;

  if(route) {
    showACHTemplateCreatedBanner = route.showACHTemplateCreatedBanner ?? ui.showCreatedACHTemplateBanner;
    createdTemplate = route.achTemplate;
    window.history.replaceState({}, document.title);
  }

  const companyAccounts = data.companyAccounts.reduce((acc, account) => {
    acc.push(<CustomSelectItem key={ account.id } value={ account.id }>{ account.nickname } - { account.number }</CustomSelectItem>);
    return acc;
  }, []);

  const linkedAccountItems = data.businessAccounts.reduce((acc, account, index) => {
    const label = `${ account.number } ${ account.nickname }`;
    if(index === 0) {
      acc.push(<CustomSelectItem value="unbalanced">UNBALANCED</CustomSelectItem>);
    }
    acc.push(<CustomSelectItem value={ account.id }>{ label }</CustomSelectItem>);
    return acc;
  }, []);

  const sortedTemplates = templates.sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    if (aName < bName) return -1;
    if (aName > bName) return 1;
    return 0;
  })

  const rows = sortedTemplates.reduce((acc, item) => {
    acc.push(
      <TemplateACHTableRow key={ item.id } template={ item } />
    );
    return acc;
  }, []);

  return (
    <PageContentView>
    <CenterContentContainer>
    { showACHTemplateCreatedBanner && 
    <ConfirmBannerView text={ `The "${ createdTemplate ? createdTemplate._name : "" }" template has been added.`} icon={<SVGAsset id="InfoIcon" className="set-width icon checkmark-icon saved-changes" />} dataPoint="Template_Added_Banner"/>
    }
      <ContentHeaderView text="ACH Templates"  dataPoint="ACH_Templates_Header" />
      <FormContainer className="small-form-width">
        { ui.searchOptions.show && 
        <FormRowView className="search-options-checkbox-row">
          <UICheckbox label="Show Search Options" value={ data.showSearchOptions ? "checked" : "" } onChange={ onChange("showSearchOptions") } dataPoint="Show_Search_Options" />
        </FormRowView>
        }
        { data.showSearchOptions &&
        <div className="search-options-dropdown">
          <FormRowView text="Company" className="company-select-row">
            <CustomSelect value={ search.companyID } onChange={ onSearchChange("companyID") }>
              { companyAccounts }
            </CustomSelect>
          </FormRowView>
          <FormRowView text="Transaction Type" className="transaction-type-select-row">
            <CustomSelect value={ search.transactionType } 
            onChange={ onSearchChange("transactionType") }>
                <CustomSelectItem value="ppd credit - consumer credit">PPD Credit - Consumer Credit</CustomSelectItem>
                <CustomSelectItem value="ppd debit - consumer debit">PPD Debit - Consumer Debit</CustomSelectItem>
                <CustomSelectItem value="ccd credit - non-consumer credit">CCD Credit - Non-Consumer Credit</CustomSelectItem>
                <CustomSelectItem value="ccd debit - non-consumer debit">CCD Debit - Non-Consumer Debit</CustomSelectItem>
                <CustomSelectItem value="tax payments">Tax Payments</CustomSelectItem>
                <CustomSelectItem value="rck - re-presentation check">RCK - Re-Presentation Check</CustomSelectItem>
                <CustomSelectItem value="web - consumer debit">WEB - Consumer Debit</CustomSelectItem>
                <CustomSelectItem value="tel - telephone-initiated debit">TEL - Telephone-Initiated Debit</CustomSelectItem>
                <CustomSelectItem value="ctx debit - corporate trade exchange debit">CTX Debit - Corporate Trade Exchange Debit</CustomSelectItem>
                <CustomSelectItem value="ctx credit - corporate trade exchange credit">CTX Credit - Corporate Trade Exchange Credit</CustomSelectItem>
                <CustomSelectItem value="child support payments">Child Support Payments</CustomSelectItem>
            </CustomSelect>
          </FormRowView>
          <FormRowView text="Participant Name" className="participant-input-row">
            <UITextInput value={ search.participantName } onChange={ onSearchChange("participantName") }/>
          </FormRowView>
          <FormRowView text="Template Name" className="template-name-input-row">
            <UITextInput value={ search.templateName } onChange={ onSearchChange("templateName") }/>
          </FormRowView>
          <FormRowView text="Offset Account" className="offset-account-select-row">
            <CustomSelect value={ search.offsetAccountID } onChange={ onSearchChange("offsetAccountID") }>
              { linkedAccountItems }
            </CustomSelect>
          </FormRowView>
          <FormButtonRow>
            <DisabledPrompt>
              <SecondaryButton text="Clear" />
            </DisabledPrompt>
            <DisabledPrompt>
              <PrimaryButton text="Search" />
            </DisabledPrompt>
          </FormButtonRow>
        </div>
        }
      </FormContainer>
      <div className="show-on-desktop">
        <TableView>
          <TableColumnContainer>
            <TableColumnView 
              className="checkbox-column text-left"
              text={
                <DisabledPrompt>
                  <UICheckbox value={ data.allRowsChecked } />
                </DisabledPrompt>
              } 
            />
            <TableColumnView 
              className="template-column text-left"
              text={
                <>
                  <span>Template</span>
                  <DisabledPrompt>
                    <SVGAsset id="ArrowUpIcon" className="clickable" />
                  </DisabledPrompt>
                </>
              } 
            />
            <TableColumnView 
              className="created-column text-left"
              text={
                <>
                  <span>Created</span>
                  <DisabledPrompt>
                    <SVGAsset id="SortIcon" className="clickable" />
                  </DisabledPrompt>
                </>
              } 
            />
            <TableColumnView 
              className="company-column text-left"
              text={
                <span>Company</span>
              } 
            />
            <TableColumnView 
              className="transaction-type-column text-left"
              text={
                <>
                  <span>SEC Code</span>
                  <DisabledPrompt>
                    <SVGAsset id="SortIcon" className="clickable" />
                  </DisabledPrompt>
                </>
              } 
            />
            <TableColumnView 
              className="amount-column text-left"
              text={
                <>
                  <span>Credit Amount</span>
                  <DisabledPrompt>
                    <SVGAsset id="SortIcon" className="clickable" />
                  </DisabledPrompt>
                </>
              } 
            />
            <TableColumnView 
              className="amount-column text-left"
              text={
                <>
                  <span>Debit Amount</span>
                  <DisabledPrompt>
                    <SVGAsset id="SortIcon" className="clickable" />
                  </DisabledPrompt>
                </>
              } 
            />
            <TableColumnView 
              className="number-column text-left"
              text={
                <>
                  <span>#</span>
                  <DisabledPrompt>
                    <SVGAsset id="SortIcon" className="clickable" />
                  </DisabledPrompt>
                </>
              } 
            />
            <TableColumnView 
              className="effective-date-column text-left"
              text={
                <span>Effective Date</span>
              } 
            />
          </TableColumnContainer>
          <TableBody>
            {/* No data for now, but these rows would be an array of components. */}
            { hasACHTemplates && rows }
            { !hasACHTemplates &&
            <TemplateACHNoTableRows />
            }
          </TableBody>
        </TableView>
      </div>
      <div className="show-on-mobile">
        <MobileTemplateACHTable templates={ templates } />
      </div>
      <div className="main-button-container flex-container flex-horizontal align-center">
        <PrimaryButton text="Initiate Same Date" className="disabled-button initiate-same-date-button" dataPoint="Initiate_Same_Date_Button" />
        <PrimaryButton text="Initiate" className="disabled-button initiate-button" dataPoint="Initiate_Button" />
        <PrimaryButton text="Delete Selected Templates" className="disabled-button delete-selected-templates-button" dataPoint="Delete_Selected_Templates_Button" />
        { ui.addTemplateButton.show && <PrimaryButton text="Add Template" className="add-template-button" dataPoint="Add_Template_Button"  onClick={() => navigate("/addACHTemplateWorkflow")} /> }
      </div>
    </CenterContentContainer>
  </PageContentView>
  );
}

/**
 * @function TemplateACHTableRow
 * @returns {React.Component}
 * @exported false
 * @description This is the table row that shows ACH Template information in the TemplateACHPage.
 */
function TemplateACHTableRow(props) {
  const template = props.template ?? {};
  const numberOfParticipants = template.achToParticipants ? template.achToParticipants.length : 0;
  const setScheduleThisBatchLink =  numberOfParticipants > 0;

  return(
    <TableItemContainer>
      <TableItemView>
        <DisabledPrompt>
          <UICheckbox />
        </DisabledPrompt>
      </TableItemView>
      <TableItemTextView 
        text={
          <>
            <p><b >Name: </b><span  data-point={`Template_Name_${template.id}`}>{template.name}</span> </p>
            <p><b>Transaction Type: </b><span className="transaction-type">{ template.transactionType }</span></p>
          </>
        } 
      />
      <TableItemTextView 
        text={
          <>
            <p>{ template.createdDate.format("m/d/Y") } </p>
          </>
        } 
      />
      <TableItemTextView text={ template.company.name } dataPoint={`Template_Company_Name_${template.id}`} />
      <TableItemTextView text={ template.secCode } />
      <TableItemTextView text={ template.creditAmount.format() } />
      <TableItemTextView text={ template.debitAmount.format() } />
      <TableItemTextView text={ numberOfParticipants } />
      <TableItemView className="effective-date-select" >
        <DateSelect value={ template.effectiveDate } dataPoint={`Template_Effective_Date_Select_${template.id}`} />
        {setScheduleThisBatchLink && 
          <DisabledPrompt>
          <a className="schedule-this-batch-link clickable" data-point={`Schedule This Batch_Link_${template.id}`}>Schedule This Batch</a>
          </DisabledPrompt>
        }
      </TableItemView>
      <TableItemView className="text-right">
        <DisabledPrompt>
          <SVGAsset id="CopyIcon" className="clickable table-icon copy-icon" data-point={`Template_Copy_Icon_${template.id}`}/>
        </DisabledPrompt>
        <DisabledPrompt>
          <SVGAsset id="EditIcon" className="clickable table-icon edit-icon" data-point={`Template_Edit_Icon_${template.id}`} />
        </DisabledPrompt>
        <DisabledPrompt>
          <SVGAsset id="TrashcanXIcon" className="clickable table-icon delete-icon" data-point={`Template_Trash_Can_Icon_${template.id}`} />
        </DisabledPrompt>
      </TableItemView>
    </TableItemContainer>
  );
}

/**
 * @function TemplateACHNoTableRows
 * @returns {React.Component}
 * @exported false
 * @description This is the table row that is shown when there are no ACH templates to display.
 */
function TemplateACHNoTableRows(props) {
  return(
    <tr className="template-ach-no-table-rows table-item-container">
      <td className="text-center" colSpan="8">There are no templates to display.</td>
    </tr>
  );
}